import React, { useEffect, useState } from "react";
import { adminAxios, errText, useAuth } from "../AuthContext";
import { Input, Textarea, Toggle, FileUploader } from "./FormFields";
import { Loader2, Save, Image as ImageIcon, X, Palette, Sparkles, Building2, Mail, Instagram, Facebook, Twitter, LayoutGrid, Home, Calendar, MapPin, Route, Box, Copy, ExternalLink, Check } from "lucide-react";

const SECTIONS = [
    { id: "brand", label: "Brand & Logo", icon: Sparkles },
    { id: "hero", label: "Hero Home", icon: ImageIcon },
    { id: "borgo", label: "Il Borgo", icon: Building2 },
    { id: "contact", label: "Contatti & Social", icon: Mail },
    { id: "modules", label: "Moduli Attivi", icon: LayoutGrid },
    { id: "theme", label: "Tema Colori", icon: Palette },
];

export default function TenantSettingsPanel() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");
    const [section, setSection] = useState("brand");

    const tenantId = user?.tenant_id;

    const load = async () => {
        if (!tenantId) { setBusy(false); return; }
        setBusy(true); setErr("");
        try {
            const { data: t } = await adminAxios().get(`/admin/tenants/${tenantId}`);
            setData(t);
        } catch (e) {
            setErr(errText(e, "Errore caricamento borgo"));
        } finally { setBusy(false); }
    };

    useEffect(() => { load(); }, [tenantId]);

    const upd = (path, value) => {
        setData((prev) => {
            const copy = JSON.parse(JSON.stringify(prev));
            const keys = path.split(".");
            let cur = copy;
            for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
            cur[keys[keys.length - 1]] = value;
            return copy;
        });
    };

    const save = async () => {
        setSaving(true); setMsg(""); setErr("");
        try {
            const { data: fresh } = await adminAxios().put(`/admin/tenants/${tenantId}`, data);
            // Only hydrate from response if it looks like a tenant (has id/slug),
            // to avoid wiping the form if backend returns just {ok:true}.
            if (fresh && fresh.id && fresh.slug) setData(fresh);
            setMsg("✔ Modifiche salvate. Ricarica la pagina pubblica per vederle.");
            setTimeout(() => setMsg(""), 6000);
        } catch (e) {
            setErr(errText(e, "Errore salvataggio"));
        } finally { setSaving(false); }
    };

    const isSuperAdmin = user?.role === "super_admin";
    const [copied, setCopied] = useState(false);
    const sanitizeSlugPreview = (raw) => {
        if (!raw) return "";
        let s = String(raw).trim().toLowerCase();
        s = s.replace(/^https?[:/-]+/i, "").replace(/^www\./i, "");
        s = s.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").replace(/-+/g, "-");
        return s.slice(0, 40);
    };

    if (!tenantId) {
        return (
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-6 text-amber-900" data-testid="tenant-settings-noaccess">
                Il super admin non ha un borgo associato. Impersona un borgo dalla dashboard Super Admin per personalizzarlo.
            </div>
        );
    }

    if (busy) return <div className="text-center py-16"><Loader2 size={26} className="animate-spin mx-auto text-emerald-600" /></div>;
    if (err && !data) return <div className="rounded-2xl bg-red-50 border border-red-200 p-6 text-red-800" data-testid="tenant-settings-err">{err}</div>;
    if (!data) return null;

    return (
        <div className="space-y-6" data-testid="tenant-settings-panel">
            {/* Sticky header: sempre visibile mentre scrolli le tab lunghe (es. Contatti & Social) */}
            <div className="sticky top-0 z-30 -mx-6 md:-mx-8 px-6 md:px-8 py-4 bg-white/95 backdrop-blur border-b border-stone-200 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <h2 className="font-serif text-2xl font-semibold text-stone-900">Personalizzazione Borgo</h2>
                    <p className="text-sm text-stone-500 mt-1">Logo, hero, descrizione, gallery, contatti e tema del tuo borgo.</p>
                </div>
                <div className="flex items-center gap-3">
                    {msg && <span className="text-sm text-emerald-700 font-medium" data-testid="tenant-settings-msg">{msg}</span>}
                    {err && <span className="text-sm text-red-700 font-medium">{err}</span>}
                    <button
                        onClick={save}
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm disabled:opacity-60 shadow-md"
                        data-testid="tenant-settings-save"
                    >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        Salva Modifiche
                    </button>
                </div>
            </div>

            {/* Public URL card: link diretto senza login per gli utenti finali */}
            {data.slug && (() => {
                // Prefer the current window origin (works for both localhost, preview, and prod)
                const origin = typeof window !== "undefined" ? window.location.origin : "";
                const publicUrl = `${origin}/#/?borgo=${encodeURIComponent(data.slug)}`;
                const copyToClipboard = async () => {
                    try {
                        await navigator.clipboard.writeText(publicUrl);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                    } catch (_) {
                        window.prompt("Copia il link:", publicUrl);
                    }
                };
                return (
                    <div className="rounded-2xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-4 md:p-5 flex items-start gap-4 flex-wrap" data-testid="tenant-public-url-card">
                        <div className="w-11 h-11 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
                            <ExternalLink size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-bold uppercase tracking-widest text-emerald-800 mb-1">Link pubblico del borgo</div>
                            <p className="text-[11px] text-stone-600 mb-2">Condividi questo URL con i visitatori: apre direttamente la homepage del borgo senza richiedere login.</p>
                            <div className="flex items-center gap-2 bg-white border border-emerald-200 rounded-lg px-3 py-2 max-w-full overflow-hidden">
                                <input
                                    type="text"
                                    value={publicUrl}
                                    readOnly
                                    onClick={(e) => e.target.select()}
                                    className="flex-1 min-w-0 bg-transparent text-sm font-mono text-stone-800 outline-none"
                                    data-testid="tenant-public-url-input"
                                />
                                <button
                                    onClick={copyToClipboard}
                                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shrink-0"
                                    data-testid="tenant-public-url-copy"
                                >
                                    {copied ? <><Check size={12} /> Copiato</> : <><Copy size={12} /> Copia</>}
                                </button>
                            </div>
                        </div>
                        <a
                            href={publicUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-stone-800 hover:bg-stone-900 text-white text-xs font-semibold shrink-0"
                            data-testid="tenant-public-url-open"
                        >
                            <ExternalLink size={12} /> Apri
                        </a>
                    </div>
                );
            })()}

            {/* section tabs */}
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
                <div className="flex flex-wrap gap-0 border-b border-stone-200 bg-stone-50">
                    {SECTIONS.map((s) => {
                        const Icon = s.icon;
                        const on = section === s.id;
                        return (
                            <button
                                key={s.id}
                                onClick={() => setSection(s.id)}
                                className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                                    on ? "border-emerald-600 text-emerald-700 bg-white" : "border-transparent text-stone-600 hover:text-emerald-700"
                                }`}
                                data-testid={`tenant-section-${s.id}`}
                            >
                                <Icon size={14} />
                                {s.label}
                            </button>
                        );
                    })}
                </div>

                <div className="p-6 space-y-5">
                    {section === "brand" && (
                        <div className="space-y-5" data-testid="tenant-brand-panel">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <Input label="Nome Brand" value={data.name} onChange={(v) => upd("name", v)} testid="t-name" placeholder="VisitBorgo" />
                                <div>
                                    <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5">Slug (URL)</label>
                                    {isSuperAdmin ? (
                                        <>
                                            <input
                                                type="text"
                                                value={data.slug || ""}
                                                onChange={(e) => upd("slug", e.target.value)}
                                                className="w-full px-3 py-2 border-2 border-amber-300 rounded-lg text-sm bg-white font-mono outline-none focus:border-amber-500"
                                                placeholder="dozza-imolese"
                                                data-testid="t-slug"
                                            />
                                            {sanitizeSlugPreview(data.slug) !== (data.slug || "").toLowerCase() && (
                                                <div className="text-[11px] text-amber-700 mt-1">
                                                    Verrà salvato come: <span className="font-mono font-bold">{sanitizeSlugPreview(data.slug) || "(vuoto)"}</span>
                                                </div>
                                            )}
                                            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 mt-1.5">
                                                ⚠️ <b>Attenzione</b>: modificando lo slug cambierà l&apos;URL pubblico del borgo. I vecchi link (bookmark, condivisioni social) smetteranno di funzionare.
                                            </div>
                                        </>
                                    ) : (
                                        <div className="px-3 py-2 rounded-lg bg-stone-100 border border-stone-200 text-sm font-mono text-stone-500" data-testid="t-slug">
                                            {data.slug} <span className="ml-2 text-xs">(solo super admin può modificarlo)</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-2">Logo del Borgo (mostrato in navbar e footer)</label>
                                <div className="flex items-start gap-4">
                                    <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-emerald-300 bg-stone-50 shrink-0">
                                        {data.logo_url ? (
                                            <img src={data.logo_url} alt="logo" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-stone-300"><ImageIcon size={32} /></div>
                                        )}
                                    </div>
                                    <div className="flex-1 space-y-2">
                                        <Input label="URL Logo" value={data.logo_url || ""} onChange={(v) => upd("logo_url", v)} testid="t-logo-url" placeholder="https://... o /uploads/..." />
                                        <div className="flex items-center gap-2">
                                            <FileUploader accept="image/*" onUploaded={(u) => upd("logo_url", u.url)} testid="t-logo-upload" />
                                            {data.logo_url && (
                                                <button onClick={() => upd("logo_url", "")} className="text-xs text-red-600 hover:underline" data-testid="t-logo-remove">Rimuovi logo</button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {section === "hero" && (
                        <div className="space-y-5" data-testid="tenant-hero-panel">
                            <Input label="Titolo Hero" value={data.hero?.title || ""} onChange={(v) => upd("hero.title", v)} testid="t-hero-title" placeholder="Tadasuni Borgo Experience" />
                            <Textarea label="Sottotitolo Hero" value={data.hero?.subtitle || ""} onChange={(v) => upd("hero.subtitle", v)} rows={2} testid="t-hero-subtitle" />
                            <Input label="Badge (etichetta piccola sopra il titolo)" value={data.hero?.badge || ""} onChange={(v) => upd("hero.badge", v)} testid="t-hero-badge" placeholder="Sardegna · Barigadu" />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <Input label="Etichetta CTA primario" value={data.hero?.cta_primary_label || ""} onChange={(v) => upd("hero.cta_primary_label", v)} testid="t-hero-cta1" placeholder="Scopri di più" />
                                <Input label="Etichetta CTA secondario" value={data.hero?.cta_secondary_label || ""} onChange={(v) => upd("hero.cta_secondary_label", v)} testid="t-hero-cta2" placeholder="Gemello Digitale" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-2">Immagine di sfondo Hero</label>
                                <div className="flex items-start gap-4">
                                    <div className="w-40 h-24 rounded-xl overflow-hidden border border-stone-200 bg-stone-50 shrink-0">
                                        {data.hero?.image_url ? (
                                            <img src={data.hero.image_url} alt="hero" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-stone-300"><ImageIcon size={28} /></div>
                                        )}
                                    </div>
                                    <div className="flex-1 space-y-2">
                                        <Input label="URL Immagine Hero" value={data.hero?.image_url || ""} onChange={(v) => upd("hero.image_url", v)} testid="t-hero-img-url" />
                                        <FileUploader accept="image/*" onUploaded={(u) => upd("hero.image_url", u.url)} testid="t-hero-img-upload" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {section === "borgo" && (
                        <div className="space-y-5" data-testid="tenant-borgo-panel">
                            <Input label="Titolo sezione Il Borgo" value={data.borgo?.title || ""} onChange={(v) => upd("borgo.title", v)} testid="t-borgo-title" />
                            <Textarea label="Descrizione Il Borgo" value={data.borgo?.description || ""} onChange={(v) => upd("borgo.description", v)} rows={5} testid="t-borgo-desc" />
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <Input label="Altitudine (m)" type="number" value={data.borgo?.altitude ?? ""} onChange={(v) => upd("borgo.altitude", v ? Number(v) : null)} testid="t-borgo-alt" />
                                <Input label="Popolazione" type="number" value={data.borgo?.population ?? ""} onChange={(v) => upd("borgo.population", v ? Number(v) : null)} testid="t-borgo-pop" />
                                <Input label="Regione" value={data.borgo?.region || ""} onChange={(v) => upd("borgo.region", v)} testid="t-borgo-region" placeholder="Barigadu, Sardegna centrale" />
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Gallery del Borgo ({data.borgo?.gallery?.length || 0} foto)</label>
                                    <FileUploader
                                        accept="image/*"
                                        multiple
                                        onUploaded={(list) => upd("borgo.gallery", [...(data.borgo?.gallery || []), ...list.map(l => l.url)])}
                                        testid="t-borgo-gallery-upload"
                                    />
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {(data.borgo?.gallery || []).map((img, i) => (
                                        <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-stone-200 group">
                                            <img src={img.startsWith('http') ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`} alt="" className="w-full h-full object-cover" />
                                            <button
                                                type="button"
                                                onClick={() => upd("borgo.gallery", (data.borgo?.gallery || []).filter((_, k) => k !== i))}
                                                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                data-testid={`t-borgo-gallery-remove-${i}`}
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ))}
                                    {(data.borgo?.gallery || []).length === 0 && (
                                        <div className="col-span-full text-center py-8 text-sm text-stone-500 bg-stone-50 rounded-xl border border-dashed border-stone-300">Nessuna foto in gallery. Aggiungine per popolare la sezione &quot;Il Borgo&quot; della home.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {section === "contact" && (
                        <div className="space-y-5" data-testid="tenant-contact-panel">
                            {/* Sezione Contatti Ente (nuovo CMS) */}
                            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-4">
                                <div>
                                    <div className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Contatti Ente</div>
                                    <p className="text-[11px] text-stone-600 mt-0.5">Dati istituzionali mostrati sulla pagina pubblica &quot;Contatti&quot; sopra il form di contatto.</p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Input label="Ente" value={data.contact?.ente || ""} onChange={(v) => upd("contact.ente", v)} testid="t-cont-ente" placeholder="Comune di Tadasuni" />
                                    <Input label="Referente" value={data.contact?.referente || ""} onChange={(v) => upd("contact.referente", v)} testid="t-cont-ref" placeholder="Sig. Mario Rossi — Sindaco" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <Input label="Telefono fisso" value={data.contact?.tel_fisso || ""} onChange={(v) => upd("contact.tel_fisso", v)} testid="t-cont-tel" placeholder="+39 0785 ..." />
                                    <Input label="Cellulare" value={data.contact?.cellulare || ""} onChange={(v) => upd("contact.cellulare", v)} testid="t-cont-cell" placeholder="+39 320 ..." />
                                    <Input label="PEC" value={data.contact?.pec || ""} onChange={(v) => upd("contact.pec", v)} testid="t-cont-pec" placeholder="protocollo@pec.comune.it" />
                                </div>
                            </div>

                            {/* Sezione contatti pubblici + indirizzo */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <Input label="Email pubblica" value={data.contact?.email || ""} onChange={(v) => upd("contact.email", v)} testid="t-cont-email" placeholder="info@visitborgo.it" />
                                <Input label="Telefono pubblico (breve)" value={data.contact?.phone || ""} onChange={(v) => upd("contact.phone", v)} testid="t-cont-phone" placeholder="+39 ..." />
                            </div>
                            <Input label="Indirizzo" value={data.contact?.address || ""} onChange={(v) => upd("contact.address", v)} testid="t-cont-addr" placeholder="Via Roma 12, 09080 Tadasuni (OR), Sardegna, Italia" />
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <Input label="CAP" value={data.contact?.cap || ""} onChange={(v) => upd("contact.cap", v)} testid="t-cont-cap" placeholder="09080" />
                                <Input label="Latitudine" type="number" step="any" value={data.contact?.lat ?? ""} onChange={(v) => upd("contact.lat", v === "" ? null : parseFloat(v))} testid="t-cont-lat" placeholder="40.0833" />
                                <Input label="Longitudine" type="number" step="any" value={data.contact?.lng ?? ""} onChange={(v) => upd("contact.lng", v === "" ? null : parseFloat(v))} testid="t-cont-lng" placeholder="8.9167" />
                            </div>

                            <div className="pt-4 border-t border-stone-100">
                                <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Social Network</div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider mb-1.5 flex items-center gap-1.5"><Instagram size={12} /> Instagram</label>
                                        <input type="text" value={data.social?.instagram || ""} onChange={(e) => upd("social.instagram", e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm bg-white outline-none focus:border-emerald-500" placeholder="https://instagram.com/..." data-testid="t-soc-ig" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider mb-1.5 flex items-center gap-1.5"><Facebook size={12} /> Facebook</label>
                                        <input type="text" value={data.social?.facebook || ""} onChange={(e) => upd("social.facebook", e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm bg-white outline-none focus:border-emerald-500" placeholder="https://facebook.com/..." data-testid="t-soc-fb" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider mb-1.5 flex items-center gap-1.5"><Twitter size={12} /> Twitter/X</label>
                                        <input type="text" value={data.social?.twitter || ""} onChange={(e) => upd("social.twitter", e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm bg-white outline-none focus:border-emerald-500" placeholder="https://twitter.com/..." data-testid="t-soc-tw" />
                                    </div>
                                </div>
                            </div>

                            {/* Amenity Vista flags disponibili per questo borgo */}
                            <div className="pt-4 border-t border-stone-100">
                                <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Caratteristiche panoramiche disponibili</div>
                                <p className="text-[11px] text-stone-500 mb-2">Scegli quali flag &quot;Vista&quot; si possono impostare sugli immobili di questo borgo e usare come filtro pubblico.</p>
                                <div className="flex flex-wrap gap-2" data-testid="t-vista-flags">
                                    {[
                                        { code: "lago", label: "Lago", emoji: "🏞️" },
                                        { code: "mare", label: "Mare", emoji: "🌊" },
                                        { code: "montagna", label: "Montagna", emoji: "⛰️" },
                                    ].map(({ code, label, emoji }) => {
                                        const list = data.available_vista_flags || ["lago", "mare", "montagna"];
                                        const on = list.includes(code);
                                        return (
                                            <button
                                                key={code}
                                                type="button"
                                                onClick={() => upd("available_vista_flags", on ? list.filter((x) => x !== code) : [...list, code])}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${on ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-stone-700 border-stone-300 hover:border-emerald-400"}`}
                                                data-testid={`t-vista-flag-${code}`}
                                            >
                                                <span>{emoji}</span> Vista {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {section === "theme" && (
                        <div className="space-y-5" data-testid="tenant-theme-panel">
                            <div className="text-sm text-stone-600 mb-3">I colori del tema vengono usati per bottoni, evidenziazioni, badge sulla home pubblica.</div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                {[
                                    { key: "primary_color", label: "Primario", def: "#059669" },
                                    { key: "accent_color", label: "Accento", def: "#d97706" },
                                    { key: "background_color", label: "Sfondo", def: "#fbf8f1" },
                                ].map((c) => (
                                    <div key={c.key}>
                                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5">{c.label}</label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="color"
                                                value={data.theme?.[c.key] || c.def}
                                                onChange={(e) => upd(`theme.${c.key}`, e.target.value)}
                                                className="w-14 h-10 rounded-lg border border-stone-200 cursor-pointer"
                                                data-testid={`t-theme-${c.key}`}
                                            />
                                            <input
                                                type="text"
                                                value={data.theme?.[c.key] || c.def}
                                                onChange={(e) => upd(`theme.${c.key}`, e.target.value)}
                                                className="flex-1 px-3 py-2 border border-stone-200 rounded-lg text-sm bg-white outline-none font-mono focus:border-emerald-500"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {section === "modules" && (
                        <div className="space-y-4" data-testid="tenant-modules-panel">
                            <div>
                                <div className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-1">Sezioni pubbliche attive</div>
                                <p className="text-sm text-stone-600">Scegli quali sezioni del sito pubblico saranno visibili nel menu di questo borgo. Le sezioni disattivate spariscono dalla navbar e le loro rotte non caricano contenuti.</p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {[
                                    { key: "immobili", label: "Immobili", icon: Home, desc: "Catalogo case in vendita/affitto" },
                                    { key: "eventi", label: "Eventi", icon: Calendar, desc: "Sagre, fiere e appuntamenti" },
                                    { key: "attrattori", label: "Attrattori", icon: MapPin, desc: "Musei, luoghi di interesse, POI" },
                                    { key: "itinerari", label: "Itinerari AI", icon: Route, desc: "Wizard AI multi-giorno" },
                                    { key: "digital_twin", label: "Gemello Digitale", icon: Box, desc: "Tour virtuale 3D (Matterport/MPSKIN)" },
                                ].map(({ key, label, icon: Icon, desc }) => {
                                    const on = (data.modules || {})[key] !== false;
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => upd(`modules.${key}`, !on)}
                                            className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition ${on ? "bg-emerald-50 border-emerald-400" : "bg-stone-50 border-stone-200 hover:border-stone-300 opacity-70"}`}
                                            data-testid={`t-mod-${key}`}
                                        >
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${on ? "bg-emerald-600 text-white" : "bg-stone-200 text-stone-500"}`}>
                                                <Icon size={18} />
                                            </div>
                                            <div className="flex-1">
                                                <div className={`text-sm font-semibold ${on ? "text-emerald-900" : "text-stone-600"}`}>{label}</div>
                                                <div className="text-[11px] text-stone-500 mt-0.5">{desc}</div>
                                                <div className={`text-[10px] font-bold uppercase tracking-widest mt-1.5 ${on ? "text-emerald-700" : "text-stone-400"}`}>
                                                    {on ? "✓ Attivo" : "○ Disattivo"}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* LIVE PREVIEW: mostra come apparirà navbar + home sections */}
                            <div className="mt-6 rounded-2xl border-2 border-dashed border-emerald-300 bg-stone-50/60 overflow-hidden" data-testid="modules-live-preview">
                                <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-200 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-800">Live Preview del sito pubblico</span>
                                    </div>
                                    <span className="text-[10px] text-stone-500">aggiornato in tempo reale</span>
                                </div>

                                {/* Fake browser chrome */}
                                <div className="px-4 py-2 flex items-center gap-1.5 bg-white border-b border-stone-200">
                                    <span className="w-2.5 h-2.5 rounded-full bg-red-300" />
                                    <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
                                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
                                    <span className="ml-3 text-[10px] font-mono text-stone-400 truncate flex-1">
                                        {(process.env.REACT_APP_BACKEND_URL || "https://…").replace(/https?:\/\//, "")}/#/?borgo={data.slug}
                                    </span>
                                </div>

                                {/* Simulated navbar */}
                                <div className="px-4 py-3 bg-white flex items-center justify-between gap-3 border-b border-stone-100">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div className="w-7 h-7 rounded-full overflow-hidden ring-1 ring-emerald-500/30 shrink-0">
                                            {data.logo_url ? (
                                                <img src={data.logo_url} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-[10px] font-bold">
                                                    {(data.name || "B").charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                        </div>
                                        <span className="font-serif text-sm font-semibold text-emerald-700 truncate">{data.name || "Nome Borgo"}</span>
                                    </div>
                                    <div className="flex items-center gap-3 flex-wrap justify-end">
                                        {[
                                            { key: "borgo", label: "Il Borgo", always: true },
                                            { key: "digital_twin", label: "Gemello Digitale" },
                                            { key: "attrattori", label: "Attrazioni" },
                                            { key: "immobili", label: "Immobili" },
                                            { key: "itinerari", label: "Itinerari" },
                                            { key: "eventi", label: "Eventi" },
                                            { key: "contatti", label: "Contatti", always: true },
                                        ].filter((l) => l.always || (data.modules || {})[l.key] !== false).map((l) => (
                                            <span key={l.label} className="text-[11px] font-semibold text-stone-700" data-testid={`preview-nav-${l.key}`}>
                                                {l.label}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Simulated home sections stack */}
                                <div className="p-3 space-y-2">
                                    {[
                                        { key: "hero", label: "Hero (foto grande)", always: true, cls: "bg-gradient-to-r from-emerald-500 to-emerald-700 text-white" },
                                        { key: "borgo_section", label: "Il Borgo (about)", always: true, cls: "bg-stone-100" },
                                        { key: "digital_twin", label: "Gemello Digitale (tour 3D)", cls: "bg-stone-100" },
                                        { key: "attrattori", label: "Attrattori (preview)", cls: "bg-stone-100" },
                                        { key: "immobili", label: "Immobili (mappa teaser)", cls: "bg-stone-100" },
                                        { key: "eventi", label: "Eventi (preview)", cls: "bg-stone-100" },
                                        { key: "contact_section", label: "Contatti + Form", always: true, cls: "bg-stone-100" },
                                    ].map((s) => {
                                        const hidden = !s.always && (data.modules || {})[s.key] === false;
                                        if (hidden) {
                                            return (
                                                <div key={s.key} className="px-3 py-1.5 rounded-lg border border-dashed border-stone-300 text-[10px] text-stone-400 italic" data-testid={`preview-section-hidden-${s.key}`}>
                                                    ○ {s.label} — nascosto
                                                </div>
                                            );
                                        }
                                        return (
                                            <div key={s.key} className={`px-3 py-2 rounded-lg ${s.cls} text-[11px] font-semibold`} data-testid={`preview-section-visible-${s.key}`}>
                                                {s.label}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Bottom save button — always visible at the end of every tab */}
                    <div className="mt-6 pt-5 border-t border-stone-200 flex items-center justify-end gap-3">
                        {msg && <span className="text-sm text-emerald-700 font-medium">{msg}</span>}
                        {err && <span className="text-sm text-red-700 font-medium">{err}</span>}
                        <button
                            onClick={save}
                            disabled={saving}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm disabled:opacity-60"
                            data-testid="tenant-settings-save-bottom"
                        >
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                            Salva Modifiche
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
